import React, { useMemo, useRef, useCallback, useState, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ThreadComposer from "./ThreadComposer";
import ScheduleSection from "./ScheduleSection";
import CollectionSelector from "./CollectionSelector";
import { ButtonBasic } from "../button/ButtonBasic";
import { isTextOverLimit } from "../../utils/textColorUtils";

// Focus 모드 전용 스레드 에디터 컴포넌트
const FocusEditor = ({ threads, onThreadsChange, theme }) => {
  const containerRef = useRef(null);
  const textAreaRefs = useRef({});

  // 메인 스레드의 첫 문장을 제목으로 추출 - useMemo로 최적화
  const title = useMemo(() => {
    const mainThread = threads.find((t) => t.isMain);
    if (!mainThread || !mainThread.text.trim()) {
      return "제목 없음";
    }

    // 첫 번째 문장 추출 (마침표, 느낌표, 물음표, 줄바꿈으로 구분)
    const text = mainThread.text.trim();
    const sentences = text.split(/[.!?\n]/);
    const firstSentence = sentences[0].trim();

    // 첫 문장이 너무 길면 50자로 제한
    if (firstSentence.length > 50) {
      return firstSentence.substring(0, 50) + "...";
    }

    return firstSentence || "제목 없음";
  }, [threads]);

  // 스레드 텍스트 변경
  const handleTextChange = useCallback((threadId, text) => {
    if (onThreadsChange) {
      const updatedThreads = threads.map((thread) =>
        thread.id === threadId ? { ...thread, text } : thread
      );
      onThreadsChange(updatedThreads);
    }
  }, [threads, onThreadsChange]);

  // 새 스레드 추가
  const addThread = () => {
    if (onThreadsChange) {
      const newThread = {
        id: Date.now(),
        text: "",
        isMain: false,
      };
      onThreadsChange([...threads, newThread]);
    }
  };

  // 스레드 삭제
  const removeThread = (threadId) => {
    if (onThreadsChange && threads.length > 1) {
      const confirmed = window.confirm("이 문단을 삭제하시겠습니까?");
      if (confirmed) {
        onThreadsChange(threads.filter((thread) => thread.id !== threadId));
      }
    }
  };

  // 텍스트 변경 시 자동 높이 조정
  useEffect(() => {
    threads.forEach((thread) => {
      const textarea = textAreaRefs.current[thread.id];
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    });
  }, [threads]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        padding: theme.spacing(6, 4),
        backgroundColor: theme.palette.background.default,
        overflow: "auto",
      }}
    >
      {/* 제목 영역 */}
      <Typography
        variant="h3"
        sx={{
          marginBottom: theme.spacing(4),
          color: theme.palette.text.primary,
          fontWeight: 700,
          lineHeight: 1.4,
          fontFamily: '"source-han-serif-korean", "Noto Serif KR", "Noto Serif CJK KR", "Source Han Serif KR", "Apple SD Gothic Neo", "Malgun Gothic", "맑은 고딕", serif',
          opacity: 0.8,
          wordBreak: "break-word",
        }}
      >
        {title}
      </Typography>

      {/* 스레드 입력 영역들 */}
      {threads.map((thread, index) => (
        <Box
          key={thread.id}
          sx={{
            marginBottom: index < threads.length - 1 ? theme.spacing(4) : 0,
          }}
        >
          {/* 스레드별 텍스트 입력 */}
          <Box
            component="textarea"
            value={thread.text}
            onChange={(e) => handleTextChange(thread.id, e.target.value)}
            placeholder={
              thread.isMain
                ? "내용을 작성해보세요..."
                : "문단을 이어서 작성해보세요..."
            }
            sx={{
              width: "100%",
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              fontSize: "22px", // 모든 문단 동일한 크기
              fontWeight: 500, // 모든 문단 동일한 굵기
              lineHeight: 1.6,
              fontFamily: theme.typography.fontFamily,
              color: theme.palette.text.primary,
              resize: "none",
              overflow: "hidden",
              minHeight: thread.isMain ? "120px" : "80px",
              padding: 0,
              margin: 0,
              "&::placeholder": {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },
              "&:focus": {
                outline: "none",
              },
              caretColor: theme.palette.primary.main,
            }}
            ref={(el) => {
              if (el) {
                textAreaRefs.current[thread.id] = el;
                // 초기 높이 설정
                el.style.height = "auto";
                el.style.height = `${el.scrollHeight}px`;
              }
            }}
            onInput={(e) => {
              // 자동 높이 조절
              e.target.style.height = "auto";
              e.target.style.height = `${e.target.scrollHeight}px`;
            }}
          />

          {/* 삭제 버튼 (메인 스레드가 아닌 경우만) */}
          {!thread.isMain && (
            <Box sx={{ textAlign: "right", marginTop: theme.spacing(1) }}>
              <Button
                size="small"
                color="error"
                onClick={() => removeThread(thread.id)}
                sx={{
                  fontSize: "12px",
                  padding: theme.spacing(0.5, 1),
                  minWidth: "auto",
                }}
              >
                삭제
              </Button>
            </Box>
          )}

          {/* 문단 구분선 (마지막 스레드가 아닌 경우) */}
          {index < threads.length - 1 && (
            <Box
              sx={{
                height: "1px",
                backgroundColor: theme.palette.divider,
                opacity: 0.3,
                marginTop: theme.spacing(3),
                marginBottom: theme.spacing(1),
              }}
            />
          )}
        </Box>
      ))}

      {/* 문단 추가 버튼 */}
      <Box sx={{ marginTop: theme.spacing(4) }}>
        <Button
          variant="outlined"
          onClick={addThread}
          sx={{
            borderColor: theme.palette.divider,
            color: theme.palette.text.secondary,
            backgroundColor: "transparent",
            "&:hover": {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.primary.main + "0a",
            },
          }}
        >
          + 문단 추가
        </Button>
      </Box>
    </Box>
  );
};

const FullScreenEditor = ({
  threads,
  onThreadsChange,
  loading,
  isScheduled,
  setIsScheduled,
  scheduleDateTime,
  setScheduleDateTime,
  collectionConfig,
  setCollectionConfig,
  myCollections,
  handlePost,
  isPostDisabled,
  hasOverLimitText,
  MAX_TEXT_LENGTH,
}) => {
  const theme = useTheme();

  // 스크롤 초기화 방지를 위한 안정적인 previewThreads 관리
  const [stablePreviewThreads, setStablePreviewThreads] = useState(threads);
  
  // 디바운싱을 통해 previewThreads 업데이트 빈도 줄이기
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const mainThread = threads.find((t) => t.isMain);
      if (!mainThread || !isTextOverLimit(mainThread.text, MAX_TEXT_LENGTH)) {
        setStablePreviewThreads(threads);
        return;
      }

      // 메인 스레드가 500자 초과인 경우 자동 분할
      const text = mainThread.text;
      const chunks = [];
      let currentChunk = "";
      const words = text.split(" ");

      for (const word of words) {
        const testChunk = currentChunk ? `${currentChunk} ${word}` : word;

        if (!isTextOverLimit(testChunk, MAX_TEXT_LENGTH)) {
          currentChunk = testChunk;
        } else {
          if (currentChunk) {
            chunks.push(currentChunk);
            currentChunk = word;
          } else {
            // 단어 자체가 500자를 초과하는 경우
            chunks.push(word.substring(0, MAX_TEXT_LENGTH));
            currentChunk = word.substring(MAX_TEXT_LENGTH);
          }
        }
      }

      if (currentChunk) {
        chunks.push(currentChunk);
      }

      // 분할된 청크로 새로운 threads 배열 생성
      const newThreads = [];
      chunks.forEach((chunk, index) => {
        newThreads.push({
          id: index + 1,
          text: chunk,
          isMain: index === 0,
        });
      });

      // 기존 댓글들 추가
      const existingReplies = threads.filter((t) => !t.isMain);
      existingReplies.forEach((reply, index) => {
        newThreads.push({
          ...reply,
          id: chunks.length + index + 1,
        });
      });

      setStablePreviewThreads(newThreads);
    }, 300); // 300ms 디바운싱

    return () => clearTimeout(timeoutId);
  }, [threads, MAX_TEXT_LENGTH]);

  return (
    <Box
      sx={{
        height: "calc(100vh - 80px)", // 헤더 높이 제외하여 fullpage 구현
        width: "100%",
        display: "flex",
        overflow: "hidden", // 페이지 레벨 스크롤 방지
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* 좌측 메인 에디터 영역 - 남은 공간 모두 사용 */}
      <Box
        sx={{
          flex: 1,
          height: "100%",
          overflow: "auto", // 텍스트 양에 따라 스크롤 발생
          backgroundColor: theme.palette.background.default,
          padding: theme.spacing(4, 8, 4, 8), // 좌측 패딩 증가
        }}
      >
        <FocusEditor
          threads={threads}
          onThreadsChange={(newThreads) => {
            // FullScreenEditor의 onThreadsChange는 (threads, helpers) 형태를 기대하므로
            // helpers 없이 threads만 전달하는 경우 빈 객체를 두 번째 인자로 전달
            onThreadsChange(newThreads, {});
          }}
          theme={theme}
        />
      </Box>

      {/* 우측 프리뷰 + 예약게시 영역 - 고정 너비로 패딩 포함 */}
      <Box
        sx={{
          width: "fit-content", // 고정 너비 (Preview 384px + 좌우 패딩)
          height: "100%",
          backgroundColor: theme.palette.grey[25],
          borderLeft: `1px solid ${theme.palette.divider}`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // 내부 요소들의 스크롤 제어
        }}
      >
        {/* 모바일 프리뷰 영역 - 동적 크기 */}
        <Box
          sx={{
            flex: 1, // 남은 공간 모두 사용
            overflow: "auto", // 글 양에 따라 스크롤 발생
            padding: theme.spacing(3, 4, 2, 4),
            minHeight: 0, // flex 아이템이 shrink 되도록 허용
            display: "flex",
            justifyContent: "center", // 중앙 정렬
          }}
        >
          {/* Preview 모드와 동일한 384px 컨테이너 */}
          <Box
            sx={{
              width: "384px", // Preview 모드와 동일한 모바일 프레임 사이즈
            }}
          >
            <ThreadComposer
              threads={stablePreviewThreads} // 디바운싱된 안정적인 threads
              onThreadsChange={() => {}} // 읽기 전용
              loading={false}
              maxThreads={10} // 프리뷰에서는 더 많은 스레드 허용
              readOnly={true}
            />
          </Box>
        </Box>

        {/* 예약게시 세팅 영역 - 항상 하단 고정 */}
        <Box
          sx={{
            flexShrink: 0, // 크기 고정
            backgroundColor: theme.palette.grey[25],
            padding: theme.spacing(3, 4, 4, 4),
            borderTop: `1px solid ${theme.palette.divider}`,
            // 스크롤 없이 항상 화면에 보이도록 보장
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* 예약 시간 설정 */}
          <Box sx={{ marginBottom: theme.spacing(3) }}>
            <ScheduleSection
              isScheduled={isScheduled}
              setIsScheduled={setIsScheduled}
              scheduleDateTime={scheduleDateTime}
              setScheduleDateTime={setScheduleDateTime}
              loading={loading}
              hideToggle={true}
            />
          </Box>

          {/* 컬렉션 선택 */}
          {isScheduled && (
            <Box sx={{ marginBottom: theme.spacing(3) }}>
              <CollectionSelector
                value={collectionConfig}
                onChange={setCollectionConfig}
                collections={myCollections}
                loading={loading}
                disabled={loading}
              />
            </Box>
          )}

          {/* 게시 버튼 */}
          <Box sx={{ marginBottom: theme.spacing(2) }}>
            <ButtonBasic
              onClick={handlePost}
              loading={loading}
              disabled={isPostDisabled}
              size="xl"
              widthType="full"
              actionType="primary"
              radius="full"
            >
              {isScheduled ? "예약 게시하기" : "게시하기"}
            </ButtonBasic>
          </Box>

          {/* 오류 메시지 */}
          {hasOverLimitText && (
            <Box
              sx={{
                fontSize: "14px",
                color: theme.palette.error.main,
                textAlign: "center",
                padding: theme.spacing(1),
                backgroundColor: theme.palette.error.light + "20",
                borderRadius: theme.spacing(0.5),
                marginBottom: theme.spacing(2),
              }}
            >
              일부 텍스트가 {MAX_TEXT_LENGTH}자를 초과했습니다. 내용을
              줄여주세요.
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FullScreenEditor;
